import * as React from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { Highlight } from "@multiagentbook/ui";
import { Testimonial } from "./Testimonials";

// Format presets for different social media platforms
type FormatPreset = "linkedin" | "square" | "twitter" | "story";

interface FormatConfig {
  name: string;
  ratio: string;
  width: number;
  height: number;
  icon: React.ReactNode;
}

const FORMAT_PRESETS: Record<FormatPreset, FormatConfig> = {
  linkedin: {
    name: "LinkedIn",
    ratio: "1.91:1",
    width: 700,
    height: 366,
    icon: (
      <svg viewBox="0 0 24 16" className="w-full h-full">
        <rect
          x="1"
          y="1"
          width="22"
          height="14"
          rx="2"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        />
      </svg>
    ),
  },
  square: {
    name: "Square",
    ratio: "1:1",
    width: 600,
    height: 600,
    icon: (
      <svg viewBox="0 0 16 16" className="w-full h-full">
        <rect
          x="1"
          y="1"
          width="14"
          height="14"
          rx="2"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        />
      </svg>
    ),
  },
  twitter: {
    name: "Twitter/X",
    ratio: "16:9",
    width: 700,
    height: 394,
    icon: (
      <svg viewBox="0 0 24 14" className="w-full h-full">
        <rect
          x="1"
          y="1"
          width="22"
          height="12"
          rx="2"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        />
      </svg>
    ),
  },
  story: {
    name: "Story",
    ratio: "9:16",
    width: 400,
    height: 711,
    icon: (
      <svg viewBox="0 0 12 22" className="w-full h-full">
        <rect
          x="1"
          y="1"
          width="10"
          height="20"
          rx="2"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        />
      </svg>
    ),
  },
};

interface TestimonialModalProps {
  isOpen: boolean;
  onClose: () => void;
  testimonial: Testimonial | null;
}

const TestimonialModal: React.FC<TestimonialModalProps> = ({
  isOpen,
  onClose,
  testimonial,
}) => {
  const [selectedFormat, setSelectedFormat] =
    React.useState<FormatPreset>("linkedin");
  const cardRef = React.useRef<HTMLDivElement>(null);

  // Handle escape key
  React.useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose]);

  if (!isOpen || !testimonial) return null;

  const format = FORMAT_PRESETS[selectedFormat];

  // Function to render text with highlights
  const renderHighlightedText = (text: string, highlights?: string[]) => {
    if (!highlights || highlights.length === 0) {
      return text;
    }

    const pattern = highlights
      .map((highlight) => highlight.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
      .join("|");

    const regex = new RegExp(`(${pattern})`, "gi");
    const parts = text.split(regex);

    return parts.map((part, index) => {
      const isHighlight = highlights.some(
        (highlight) => part.toLowerCase() === highlight.toLowerCase()
      );

      if (isHighlight) {
        return <Highlight key={index}>{part}</Highlight>;
      }
      return part;
    });
  };

  // Fixed font size - consistent regardless of text length
  const quoteFontSize = "text-lg";

  const modalContent = (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal container */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className="relative bg-primary rounded-lg border border-secondary w-full max-w-4xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header with format selector - icons only */}
          <div className="flex items-center justify-between p-4 border-b border-secondary">
            <div className="flex items-center gap-1">
              {(Object.keys(FORMAT_PRESETS) as FormatPreset[]).map((key) => {
                const preset = FORMAT_PRESETS[key];
                return (
                  <button
                    key={key}
                    onClick={() => setSelectedFormat(key)}
                    className={`
                      p-2 rounded-md transition-colors duration-150
                      ${
                        selectedFormat === key
                          ? "bg-accent/10 text-accent"
                          : "text-tertiary hover:text-secondary hover:bg-secondary/50"
                      }
                    `}
                    title={`${preset.name} (${preset.ratio})`}
                  >
                    <span className="w-6 h-5 flex items-center justify-center">
                      {preset.icon}
                    </span>
                  </button>
                );
              })}
            </div>

            <button
              onClick={onClose}
              className="text-tertiary hover:text-primary transition-colors p-1.5 rounded-md hover:bg-secondary"
              aria-label="Close modal"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content area with centered card */}
          <div className="p-6 bg-secondary/30 flex items-center justify-center min-h-[500px]">
            {/* Screenshot-ready card - book cover on left, auto-height */}
            <div
              ref={cardRef}
              className="bg-primary rounded-lg overflow-hidden flex flex-row"
              style={{
                width: `${format.width * 1.1}px`,
                minHeight: `${format.height * 0.5}px`,
                maxWidth: "100%",
                maxHeight: "85vh",
              }}
            >
              {/* Left side - Book cover */}
              <div className="w-1/4 min-w-[120px] bg-secondary/20 flex items-start justify-center p-4 pt-6">
                <img
                  src="/images/bookcover.webp"
                  alt="Designing Multi-Agent Systems"
                  className="w-full h-auto max-h-[80%] object-contain rounded"
                />
              </div>

              {/* Right side - Quote content */}
              <div className="flex-1 flex flex-col">
                {/* Card content */}
                <div className="flex-1 p-6 sm:p-8 flex flex-col">
                  {/* Author section - at top */}
                  <div className="mb-4 pb-4 border-b border-secondary/50">
                    <div className="font-bold text-primary text-xl">
                      {testimonial.author}
                    </div>
                    <div className="text-secondary text-base mt-1">
                      {testimonial.title}
                      {testimonial.company && (
                        <>
                          {" at "}
                          <span className="text-accent font-semibold">
                            {testimonial.company}
                          </span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Quote marks */}
                  <div className="text-accent/20 mb-2">
                    <svg
                      className="w-8 h-8"
                      fill="currentColor"
                      viewBox="0 0 32 32"
                    >
                      <path d="M9.352 4C4.456 7.456 1 13.12 1 19.36c0 5.088 3.072 8.064 6.624 8.064 3.36 0 5.856-2.688 5.856-5.856 0-3.168-2.208-5.472-5.088-5.472-.576 0-1.344.096-1.536.192.48-3.264 3.552-7.104 6.624-9.024L9.352 4zm16.512 0c-4.8 3.456-8.256 9.12-8.256 15.36 0 5.088 3.072 8.064 6.624 8.064 3.264 0 5.856-2.688 5.856-5.856 0-3.168-2.304-5.472-5.184-5.472-.576 0-1.248.096-1.44.192.48-3.264 3.456-7.104 6.528-9.024L25.864 4z" />
                    </svg>
                  </div>

                  {/* Quote text - grows with content */}
                  <div className={`flex-1 ${quoteFontSize}`}>
                    <p className="text-primary leading-relaxed">
                      {renderHighlightedText(
                        testimonial.quote,
                        testimonial.highlights
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default TestimonialModal;
